import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardContent,
    Grid,
    makeStyles,
    Divider,
} from '@material-ui/core';
import { Edit } from '@material-ui/icons';
import { format, isSameMinute } from 'date-fns';
import DOMPurify from 'dompurify';
import { showToast } from '@klpkonsern/ui';

import {
    Block,
    Heading,
    Tooltip,
    Typography,
    theme,
    LocalLoader,
    IconName,
    Illustration,
} from '@klp/design';
import { contextSelectors } from '@klp/contextSelector';
import {
    formatDateWithTime,
    formatRelative,
    parseSafeDate,
} from '@klp/utils/dateTimeFormatter';

import { KommentarPopper } from './KommentarPopper';
import type { IKommentar } from '../services/rtkApi';
import {
    useLazyGetKommentarerQuery,
    usePostKommentarMutation,
} from '../services/rtkApi';

import { KommentarDetaljer } from './KommentarDetaljer';
import { RedigerKommentar } from './RedigerKommentar';
import {
    kommentarerSelectorActions,
    kommentarerSelectors,
} from '../store/kommentarerSlice';

const useStyles = makeStyles(() => ({
    card: {
        padding: theme.spacing(3),
        borderRadius: theme.spacing(0.2),
        width: '100%',
        minWidth: '450px',
        overflow: 'auto',
        maxWidth: '450px',
        maxHeight: '400px',
    },
    commentWrapper: {
        width: '100%',
        minWidth: '450px',
        overflow: 'auto',
        maxWidth: '450px',
        maxHeight: '400px',
    },
    buttons: {
        width: '120px',
        paddingTop: '10px',
        paddingBottom: '10px',
        paddingLeft: '80px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'left',
        cursor: 'pointer',
    },
    cardContent: {
        maxWidth: 'inherit',
        overflowY: 'auto',
        wordWrap: 'break-word',
    },
    cardTitle: {
        maxWidth: 'inherit',
        overflowY: 'auto',
        wordWrap: 'break-word',
        flex: 1,
    },
    highlightedComment: {
        cursor: 'pointer',
        '&:hover': {
            opacity: 0.7,
        },
    },
    commentDate: {
        cursor: 'pointer',
        paddingLeft: '2.5px',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    editIcon: {
        cursor: 'pointer',
        fontSize: '20px',
        paddingLeft: '5px',

        '&:hover': {
            opacity: 0.7,
        },
    },
    editIconTooltip: {
        fontSize: '20px',
    },
}));

export const Kommentarer = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    const [chosenComment, setChosenComment] = useState<IKommentar | undefined>(
        undefined,
    );
    const { selectedContext } = useSelector(contextSelectors);
    const {
        selectedDate,
        editMode: { editFlag, kommentarId },
    } = useSelector(kommentarerSelectors);
    const {
        reducerActions: { setOpenModal, setEditMode },
    } = kommentarerSelectorActions;

    const handleClose = () => {
        dispatch(setEditMode({ editFlag: false, kommentarId }));
        dispatch(setOpenModal(false));
    };
    const [
        getKommentarer,
        {
            data: kommentarerData,
            isFetching: kommentarerIsFetching,
            isLoading: kommentarerIsLoading,
        },
    ] = useLazyGetKommentarerQuery();

    const [postKommentar, { isError: postKommentarError }] =
        usePostKommentarMutation();

    const handleModal = (kommentar: IKommentar) => {
        setChosenComment(kommentar);
    };

    const handleFremhevKommentar = async (kommentar: IKommentar) => {
        if (!kommentar) return;

        const newPost = { ...kommentar, framhevet: !kommentar?.framhevet };

        await postKommentar({ ...newPost });

        if (postKommentarError) {
            showToast({ body: t('general.error.message'), type: 'error' });
        }
    };

    useEffect(() => {
        if (selectedContext?.id) {
            getKommentarer({
                byggNummer: selectedContext?.id,
                kunfremhevde: false,
                kategori: pathname.substring(1),
                gjelderForAar: format(selectedDate, 'yyyy'),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedContext, selectedDate]);

    // handle the dilemma of the divergence between gjeldendeAar and created

    return (
        <>
            <Block paddingTop="fat" paddingRight="compact">
                <Block>
                    <LocalLoader
                        isLoading={
                            kommentarerIsLoading || kommentarerIsFetching
                        }
                    >
                        <Grid container={true} spacing={2}>
                            {kommentarerData?.map((kommentar) => {
                                const createdDateObj = parseSafeDate(
                                    kommentar?.created,
                                );
                                const updatedDateObj = parseSafeDate(
                                    kommentar?.oppdatert,
                                );

                                const createdDateString =
                                    formatDateWithTime(createdDateObj);
                                const updatedDateString =
                                    formatDateWithTime(updatedDateObj);
                                const createdRelative =
                                    formatRelative(createdDateObj);
                                const isUpdated =
                                    createdDateObj &&
                                    updatedDateObj &&
                                    !isSameMinute(
                                        createdDateObj,
                                        updatedDateObj,
                                    );

                                return (
                                    <Grid item={true} key={kommentar.id}>
                                        {editFlag &&
                                        kommentarId === kommentar.id ? (
                                            <Block
                                                className={
                                                    classes.commentWrapper
                                                }
                                            >
                                                <RedigerKommentar
                                                    onCancel={handleClose}
                                                    onSave={handleClose}
                                                />
                                            </Block>
                                        ) : (
                                            <Card className={classes.card}>
                                                <CardContent>
                                                    <Grid
                                                        container={true}
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        direction="row"
                                                        style={{
                                                            flexWrap: 'nowrap',
                                                        }}
                                                    >
                                                        <Grid item={true}>
                                                            <Block
                                                                paddingRight="compact"
                                                                onClick={() =>
                                                                    handleFremhevKommentar(
                                                                        kommentar,
                                                                    )
                                                                }
                                                            >
                                                                <Tooltip
                                                                    translation="comments.highlight"
                                                                    title=""
                                                                >
                                                                    <Illustration
                                                                        className={
                                                                            classes.highlightedComment
                                                                        }
                                                                        icon={
                                                                            kommentar?.framhevet
                                                                                ? IconName.STAR_FILLED
                                                                                : IconName.STAR
                                                                        }
                                                                        size="xsmall"
                                                                        klpPalette="secondary"
                                                                    />
                                                                </Tooltip>
                                                            </Block>
                                                        </Grid>
                                                        <Grid item={true}>
                                                            <Heading
                                                                className={
                                                                    classes.cardTitle
                                                                }
                                                                klpHeadingLevel="FOUR"
                                                            >
                                                                {
                                                                    kommentar?.tittel
                                                                }
                                                            </Heading>
                                                        </Grid>
                                                        <Grid item={true}>
                                                            <KommentarPopper
                                                                kommentar={
                                                                    kommentar
                                                                }
                                                                handleModalCallback={
                                                                    handleModal
                                                                }
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                    <Divider />
                                                    <Grid item={true}>
                                                        <Typography
                                                            variant="body2"
                                                            gutterBottom={true}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                            }}
                                                        >
                                                            {`${kommentar?.createdAvBruker?.navn}, `}
                                                            <Tooltip
                                                                title={
                                                                    createdDateString
                                                                }
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    color="secondary"
                                                                    className={
                                                                        classes.commentDate
                                                                    }
                                                                >
                                                                    {
                                                                        createdRelative
                                                                    }
                                                                </Typography>
                                                            </Tooltip>
                                                            {isUpdated && (
                                                                <Tooltip
                                                                    className={
                                                                        classes.editIconTooltip
                                                                    }
                                                                    title={`${t(
                                                                        'comments.edited',
                                                                    )} ${
                                                                        kommentar
                                                                            ?.oppdatertAvBruker
                                                                            ?.navn
                                                                    }, ${updatedDateString} `}
                                                                >
                                                                    <Edit
                                                                        className={
                                                                            classes.editIcon
                                                                        }
                                                                    />
                                                                </Tooltip>
                                                            )}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid container={true}>
                                                        <Typography
                                                            klpTypographyVariant="small"
                                                            className={
                                                                classes.cardContent
                                                            }
                                                        >
                                                            {kommentar?.tekst && (
                                                                <span
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: DOMPurify.sanitize(
                                                                            kommentar?.tekst,
                                                                        ),
                                                                    }}
                                                                />
                                                            )}
                                                        </Typography>
                                                    </Grid>
                                                </CardContent>
                                            </Card>
                                        )}
                                    </Grid>
                                );
                            })}
                        </Grid>{' '}
                    </LocalLoader>
                </Block>
            </Block>
            <KommentarDetaljer
                open={Boolean(chosenComment)}
                onClose={() => {
                    setChosenComment(undefined);
                }}
                comment={chosenComment}
            />
        </>
    );
};
